import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { MainLayout } from '../../components/layout/MainLayout'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { NumberInput } from '../../components/ui/NumberInput'
import { MaskedInput } from '../../components/ui/MaskedInput'
import { SelectModal } from '../../components/ui/SelectModal'
import { MultiSelectModal } from '../../components/ui/MultiSelectModal'
import { ProductSelectModal } from '../../components/ui/ProductSelectModal'
import { ArrowLeft, ShoppingCart, User, Building2, Plus, Trash2 } from 'lucide-react'
import { clienteService } from '../../services/clienteService'
import { industriaService } from '../../services/industriaService'
import { produtoService } from '../../services/produtoService'
import { pedidoService } from '../../services/pedidoService'
import type { Cliente, Industria, Produto, PedidoIndustria , TipoFrete } from '../../types'
import { toast } from '../../utils/toast'
import { formatDate, getIndustriaColorClasses, getIndustriaInlineStyles } from '../../utils/helpers'

export function EditarPedidoPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [industrias, setIndustrias] = useState<Industria[]>([])
  const [produtos, setProdutos] = useState<Produto[]>([])
  
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null)
  const [selectedIndustrias, setSelectedIndustrias] = useState<Industria[]>([])
  const [pedidoIndustrias, setPedidoIndustrias] = useState<PedidoIndustria[]>([])
  const [observacao, setObservacao] = useState('')
  const [createdAt, setCreatedAt] = useState('')
  const [status, setStatus] = useState<'pendente' | 'processando' | 'concluido' | 'cancelado'>('pendente')
  
  const [showClienteModal, setShowClienteModal] = useState(false)
  const [showIndustriasModal, setShowIndustriasModal] = useState(false)
  const [showProdutoModal, setShowProdutoModal] = useState(false)
  const [currentIndustriaId, setCurrentIndustriaId] = useState<string | null>(null)

  const getIndustriaStyle = (cor?: string | null) => {
    const classes = getIndustriaColorClasses(cor)
    const inline = getIndustriaInlineStyles(cor)
    return { classes, inline }
  }

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      pendente: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
      processando: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
      concluido: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
      cancelado: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
    }
    return statusColors[status] || statusColors.pendente
  }

  const getStatusLabel = (status: string) => {
    const statusLabels: Record<string, string> = {
      pendente: 'Pendente',
      processando: 'Em Processamento',
      concluido: 'Concluído',
      cancelado: 'Cancelado',
    }
    return statusLabels[status] || status
  }

  const handleStatusChange = async (newStatus: 'processando' | 'concluido' | 'cancelado') => {
    if (!id) return
    
    try {
      await pedidoService.updateStatus(id, newStatus)
      setStatus(newStatus)
      toast.success(`Status alterado para ${getStatusLabel(newStatus)}`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao alterar status'
      toast.error(message)
      console.error('Erro ao alterar status:', error)
    }
  }

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      if (!id) {
        navigate('/pedidos')
        return
      }

      const [pedidoData, clientesResult, industriasResult, produtosResult] = await Promise.all([
        pedidoService.getById(id),
        clienteService.getAll({ limit: 1000, status: 'ativo' }),
        industriaService.getAll({ limit: 1000, status: 'ativo' }),
        produtoService.getAll({ limit: 1000, status: 'ativo' }),
      ])

      if (!pedidoData) {
        toast.error('Pedido não encontrado')
        navigate('/pedidos')
        return
      }

      setClientes(clientesResult.data)
      setIndustrias(industriasResult.data)
      setProdutos(produtosResult.data)

      setSelectedCliente(pedidoData.cliente || null)
      setObservacao(pedidoData.observacao || '')
      setCreatedAt(pedidoData.createdAt || new Date().toISOString())
      setStatus(pedidoData.status)

      const industriasSelecionadas = pedidoData.industrias.map(pi => pi.industria!).filter(Boolean)
      setSelectedIndustrias(industriasSelecionadas)
      setPedidoIndustrias(
        pedidoData.industrias.map(pi => ({
          ...pi,
          observacao:
            pi.observacao ??
            pi.observacaoIndustria ??
            pi.observacao_industria ??
            undefined,
          itens: (pi.itens || []).map(item => ({
            ...item,
            preco: normalizePreco(item.preco as unknown as string | number)
          }))
        }))
      )
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar pedido'
      toast.error(message)
      console.error('Erro ao carregar pedido:', error)
      navigate('/pedidos')
    } finally {
      setLoading(false)
    }
  }, [id, navigate])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleSelectCliente = (cliente: Cliente) => {
    setSelectedCliente(cliente)
  }

  const handleSelectIndustrias = (industriasList: Industria[]) => {
    const novaIndustria = industriasList[0]
    if (!novaIndustria) return

    const industriaAtualId = pedidoIndustrias[0]?.industriaId

    // Se a indústria realmente mudou, limpar itens e observações específicas
    if (novaIndustria.id !== industriaAtualId) {
      setSelectedIndustrias([novaIndustria])
      setPedidoIndustrias([{
        industriaId: novaIndustria.id,
        industria: novaIndustria,
        observacao: '',
        ordemCompra: '',
        frete: undefined,
        itens: []
      }])
      toast.info('A indústria foi alterada. Adicione os produtos novamente.')
    }
  }

  const handleProductsConfirm = (products: Array<{ id: string; codigo: string; nome: string; quantidade?: number }>) => {
    if (!currentIndustriaId) return

    setPedidoIndustrias(prev => prev.map(pi => {
      if (pi.industriaId === currentIndustriaId) {
        const newItems = products.map(p => {
          const produtoCompleto = produtos.find(prod => prod.id === p.id)
          const existingItem = (pi.itens || []).find(item => item.produtoId === p.id)
          return {
            produtoId: p.id,
            produto: produtoCompleto || { id: p.id, codigo: p.codigo, nome: p.nome, industriaId: currentIndustriaId, ativo: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
            quantidade: p.quantidade || 1,
            preco: existingItem?.preco,
          }
        })
        return {
          ...pi,
          itens: newItems
        }
      }
      return pi
    }))
  }

  const updateQuantidade = (industriaId: string, produtoId: string, quantidade: number) => {
    if (quantidade <= 0) {
      removeProduto(industriaId, produtoId)
      return
    }

    setPedidoIndustrias(prev => prev.map(pi => {
      if (pi.industriaId === industriaId) {
        return {
          ...pi,
          itens: (pi.itens || []).map(item =>
            item.produtoId === produtoId ? { ...item, quantidade } : item
          )
        }
      }
      return pi
    }))
  }

  const parseCurrencyValue = (value: string): number | undefined => {
    const digits = value.replace(/\D/g, '')
    if (!digits) return undefined
    return parseInt(digits, 10) / 100
  }

  const normalizePreco = (preco?: number | string): number | undefined => {
    if (preco === undefined || preco === null) return undefined
    if (typeof preco === 'number') return preco
    const parsed = parseFloat(preco.replace(',', '.'))
    return Number.isNaN(parsed) ? undefined : parsed
  }

  const updatePreco = (industriaId: string, produtoId: string, preco?: number) => {
    if (preco !== undefined && preco < 0) return

    setPedidoIndustrias(prev => prev.map(pi => {
      if (pi.industriaId === industriaId) {
        return {
          ...pi,
          itens: (pi.itens || []).map(item =>
            item.produtoId === produtoId ? { ...item, preco } : item
          )
        }
      }
      return pi
    }))
  }

  const removeProduto = (industriaId: string, produtoId: string) => {
    setPedidoIndustrias(prev => prev.map(pi => {
      if (pi.industriaId === industriaId) {
        return {
          ...pi,
          itens: (pi.itens || []).filter(item => item.produtoId !== produtoId)
        }
      }
      return pi
    }))
  }

  const openProdutoModal = (industriaId: string) => {
    setCurrentIndustriaId(industriaId)
    setShowProdutoModal(true)
  }

  const getTotalItens = () => {
    return pedidoIndustrias.reduce((total, pi) => 
      total + (pi.itens || []).reduce((sum, item) => sum + item.quantidade, 0), 0
    )
  }

  const handleSalvarPedido = async () => {
    if (!selectedCliente) {
      toast.error('Selecione um cliente')
      return
    }

    if (selectedIndustrias.length === 0) {
      toast.error('Selecione pelo menos uma indústria')
      return
    }

    if (!pedidoIndustrias.some(pi => (pi.itens || []).length > 0)) {
      toast.error('Adicione pelo menos um produto ao pedido')
      return
    }

    try {
      if (!id) return

      // Validar tamanho das observações
      if (observacao && observacao.length > 500) {
        toast.error('Observação geral deve ter no máximo 500 caracteres')
        return
      }

      // Validar observações das indústrias
      const invalidObservacao = pedidoIndustrias.find(pi => pi.observacao && pi.observacao.length > 500)
      if (invalidObservacao) {
        toast.error('Observação da indústria deve ter no máximo 500 caracteres')
        return
      }

      // Preparar payload para a API
      const industriasPayload = pedidoIndustrias
        .filter(pi => pi.industriaId && (pi.itens || []).length > 0)
        .map(pi => ({
          industriaId: pi.industriaId!,
          observacao: pi.observacao || undefined,
          observacaoIndustria: pi.observacao || undefined,
          observacao_industria: pi.observacao || undefined,
          ordemCompra: pi.ordemCompra || undefined,
          frete: pi.frete || undefined,
          itens: (pi.itens || []).map(item => ({
            produtoId: item.produtoId,
            quantidade: item.quantidade,
            preco: item.preco
          }))
        }))

      await pedidoService.update(id, {
        clienteId: selectedCliente.id,
        observacao: observacao || undefined,
        industrias: industriasPayload
      })

      // Se o pedido estava pendente, mudar para processando
      if (status === 'pendente') {
        await pedidoService.updateStatus(id, 'processando')
        toast.success('Pedido atualizado e enviado para processamento!')
      } else {
        toast.success('Pedido atualizado com sucesso!')
      }

      navigate('/pedidos')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao atualizar pedido'
      toast.error(message)
      console.error('Erro ao atualizar pedido:', error)
    }
  }

  const getProdutosPorIndustria = (industriaId: string) => {
    return produtos.filter(p => p.industriaId === industriaId)
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">Carregando pedido...</p>
          </div>
        </div>
      </MainLayout>
    )
  }

  const isConcluido = status === 'concluido'

  return (
    <MainLayout>
      <div className="space-y-4 md:space-y-6 animate-fadeIn pb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 md:gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/pedidos')}
            >
              <ArrowLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Voltar</span>
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                Editar Pedido
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Criado em: {formatDate(createdAt)}
              </p>
            </div>
          </div>
        </div>

        {/* Status do Pedido */}
        <Card>
          <div className="space-y-4">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">Status do Pedido</h3>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <span className={`inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-medium ${getStatusColor(status)}`}>
                {getStatusLabel(status)}
              </span>
              {status === 'processando' && (
                <Button
                  size="md"
                  onClick={() => handleStatusChange('concluido')}
                  className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                >
                  Marcar como Concluído
                </Button>
              )}
              {status === 'concluido' && (
                <Button
                  size="md"
                  variant="outline"
                  onClick={() => handleStatusChange('processando')}
                  className="w-full sm:w-auto"
                >
                  Reabrir Pedido
                </Button>
              )}
              {status === 'pendente' && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Aguardando sincronização com o backend
                </p>
              )}
            </div>
          </div>
        </Card>

        <Card title="Cliente">
          <div className="space-y-3">
            {selectedCliente ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-lg border-2 border-primary-500 bg-primary-50 dark:bg-primary-900/20 p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-primary-100 dark:bg-primary-900/40 p-3">
                    <User className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{selectedCliente.razaoSocial}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{selectedCliente.email}</p>
                  </div>
                </div>
                {!isConcluido && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowClienteModal(true)}
                    className="w-full sm:w-auto"
                  >
                    Alterar
                  </Button>
                )}
              </div>
            ) : (
              <Button
                variant="primary"
                onClick={() => setShowClienteModal(true)}
                className="w-full min-h-[56px]"
                disabled={isConcluido}
              >
                <User className="h-5 w-5 mr-2" />
                Selecionar Cliente
              </Button>
            )}
          </div>
        </Card>

        <Card title="Indústrias">
          <div className="space-y-3">
            <Button
              variant={selectedIndustrias.length > 0 ? 'outline' : 'primary'}
              onClick={() => !isConcluido && setShowIndustriasModal(true)}
              className="w-full min-h-[56px]"
              disabled={isConcluido}
              title={isConcluido ? 'Pedido concluído não pode ser editado' : 'Clique para alterar a indústria (produtos serão removidos)'}
            >
              <Building2 className="h-5 w-5 mr-2" />
              {selectedIndustrias.length > 0
                ? `${selectedIndustrias.length} indústria(s) selecionada(s)`
                : 'Selecionar Indústrias'}
            </Button>

            {selectedIndustrias.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedIndustrias.map(industria => {
                  const { classes, inline } = getIndustriaStyle(industria.cor)

                  return (
                  <div
                    key={industria.id}
                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-1 text-sm font-medium border-2 ${classes.border} ${classes.bg} ${classes.text}`}
                    style={inline ? {
                      borderColor: inline.borderColor,
                      backgroundColor: inline.backgroundColor,
                      color: inline.color,
                    } : undefined}
                  >
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: inline ? inline.cor : classes.cor }}
                    />
                    {industria.nomeFantasia}
                  </div>
                )})}
              </div>
            )}
          </div>
        </Card>

        {pedidoIndustrias.length > 0 && (
          <Card title="Produtos">
            <div className="space-y-4">
              {pedidoIndustrias.map(pedidoIndustria => {
                if (!pedidoIndustria.industriaId) return null
                const { classes, inline } = getIndustriaStyle(pedidoIndustria.industria?.cor)

                return (
                <div
                  key={pedidoIndustria.industriaId}
                  className={`rounded-lg border-2 shadow-sm ${classes.border} ${classes.bgLight} overflow-hidden transition-shadow hover:shadow-md`}
                  style={inline ? {
                    borderColor: inline.borderColor,
                    backgroundColor: inline.backgroundColorLight,
                  } : undefined}
                >
                  <div
                    className={`p-3 md:p-4 ${classes.bg}`}
                    style={inline ? { backgroundColor: inline.backgroundColor } : undefined}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-3 w-3 rounded-full ring-2 ring-white dark:ring-gray-800"
                          style={{ backgroundColor: inline ? inline.cor : classes.cor }}
                        />
                        <h3 className={`font-semibold text-base md:text-lg ${classes.text}`} style={inline ? { color: inline.color } : undefined}>
                          {pedidoIndustria.industria?.nomeFantasia}
                        </h3>
                      </div>
                      {!isConcluido && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => pedidoIndustria.industriaId && openProdutoModal(pedidoIndustria.industriaId)}
                          className="w-full sm:w-auto"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          <span className="sm:inline">Adicionar Produto</span>
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="p-3 md:p-4">
                    {!pedidoIndustria.itens || pedidoIndustria.itens.length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-6 text-base">
                        Nenhum produto adicionado
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {/* Layout mobile-first: cards em vez de tabela */}
                        <div className="block md:hidden space-y-4">
                          {(pedidoIndustria.itens || []).map(item => {
                            const preco = normalizePreco(item.preco as unknown as string | number)
                            return (
                              <div
                                key={item.produtoId}
                                className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                      Código: {item.produto?.codigo || 'N/A'}
                                    </p>
                                    <p className="font-medium text-gray-900 dark:text-white">
                                      {item.produto?.nome || 'Produto não encontrado'}
                                    </p>
                                  </div>
                                  {!isConcluido && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        pedidoIndustria.industriaId && removeProduto(pedidoIndustria.industriaId, item.produtoId)
                                      }
                                      title="Excluir produto"
                                      className="flex-shrink-0"
                                    >
                                      <Trash2 className="h-5 w-5 text-red-500" />
                                    </Button>
                                  )}
                                </div>

                                <div className="space-y-3">
                                  <NumberInput
                                    label="Quantidade"
                                    value={item.quantidade}
                                    onChange={value =>
                                      pedidoIndustria.industriaId && updateQuantidade(
                                        pedidoIndustria.industriaId,
                                        item.produtoId,
                                        value
                                      )
                                    }
                                    min={1}
                                    showControls={!isConcluido}
                                    disabled={isConcluido}
                                  />

                                  <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                      Preço Unitário (R$)
                                    </label>
                                    <MaskedInput
                                      mask="currency"
                                      value={
                                        preco !== undefined
                                          ? preco.toFixed(2).replace('.', ',')
                                          : ''
                                      }
                                      onChange={value => {
                                        const parsed = parseCurrencyValue(value)
                                        pedidoIndustria.industriaId && updatePreco(pedidoIndustria.industriaId, item.produtoId, parsed)
                                      }}
                                      placeholder="0,00"
                                      disabled={isConcluido}
                                    />
                                  </div>

                                  {preco !== undefined && preco > 0 && (
                                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                      <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Subtotal:</span>
                                        <span className="text-lg font-bold text-gray-900 dark:text-white">
                                          R$ {(item.quantidade * preco).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                        </div>

                        {/* Layout desktop: tabela tradicional */}
                        <div className="hidden md:block overflow-x-auto">
                          <table className="w-full">
                            <thead className="border-b border-gray-200 dark:border-gray-700">
                              <tr>
                                <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                  Código
                                </th>
                                <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                  Nome
                                </th>
                                <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                  Quantidade
                                </th>
                                <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                  Preço Unitário (R$)
                                </th>
                                <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                  Subtotal (R$)
                                </th>
                                <th className="pb-3 text-right text-sm font-semibold text-gray-900 dark:text-white">
                                  Ação
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {(pedidoIndustria.itens || []).map(item => (
                                <tr
                                  key={item.produtoId}
                                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                >
                                  <td className="py-3 text-sm font-medium text-gray-900 dark:text-white">
                                    {item.produto?.codigo || 'N/A'}
                                  </td>
                                  <td className="py-3 text-base text-gray-700 dark:text-gray-300 whitespace-normal break-words max-w-md">
                                    {item.produto?.nome || 'Produto não encontrado'}
                                  </td>
                                  <td className="py-3">
                                    <NumberInput
                                      value={item.quantidade}
                                      onChange={value =>
                                        pedidoIndustria.industriaId && updateQuantidade(
                                          pedidoIndustria.industriaId,
                                          item.produtoId,
                                          value
                                        )
                                      }
                                      min={1}
                                      showControls={false}
                                      disabled={isConcluido}
                                      className="w-24"
                                    />
                                  </td>
                                  <td className="py-3">
                                    <MaskedInput
                                      mask="currency"
                                      value={
                                        normalizePreco(item.preco as unknown as string | number) !== undefined
                                          ? normalizePreco(item.preco as unknown as string | number)!
                                              .toFixed(2)
                                              .replace('.', ',')
                                          : ''
                                      }
                                      onChange={value => {
                                        const parsed = parseCurrencyValue(value)
                                        pedidoIndustria.industriaId && updatePreco(pedidoIndustria.industriaId, item.produtoId, parsed)
                                      }}
                                      placeholder="0,00"
                                      className="w-28"
                                      disabled={isConcluido}
                                    />
                                  </td>
                                  <td className="py-3 text-sm font-semibold text-gray-900 dark:text-white">
                                    {(() => {
                                      const preco = normalizePreco(item.preco as unknown as string | number)
                                      return preco !== undefined && preco > 0 ? (
                                        `R$ ${(item.quantidade * preco).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                      ) : (
                                        <span className="text-gray-400 dark:text-gray-500">-</span>
                                      )
                                    })()}
                                  </td>
                                  <td className="py-3 text-right">
                                    {!isConcluido && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          pedidoIndustria.industriaId && removeProduto(pedidoIndustria.industriaId, item.produtoId)
                                        }
                                        title="Excluir produto"
                                      >
                                        <Trash2 className="h-4 w-4 text-red-500" />
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Subtotal da Indústria */}
                    {pedidoIndustria.itens && pedidoIndustria.itens.length > 0 && (() => {
                      const subtotal = pedidoIndustria.itens.reduce((sum, item) => {
                        const preco = normalizePreco(item.preco as unknown as string | number)
                        return sum + (item.quantidade * (preco || 0))
                      }, 0)
                      
                      return subtotal > 0 ? (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex justify-end">
                            <div className="text-right">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Subtotal desta indústria: </span>
                              <span className="text-lg font-bold text-gray-900 dark:text-white">
                                R$ {subtotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : null
                    })()}

                    {/* Finalização da Indústria */}
                    {pedidoIndustria.industriaId && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                            Ordem de Compra (opcional)
                          </label>
                          <Input
                            type="text"
                            value={pedidoIndustria.ordemCompra || ''}
                            onChange={e => {
                              setPedidoIndustrias(prev => prev.map(pi =>
                                pi.industriaId === pedidoIndustria.industriaId
                                  ? { ...pi, ordemCompra: e.target.value }
                                  : pi
                              ))
                            }}
                            maxLength={100}
                            placeholder="Ex: OC-2024-001"
                            disabled={isConcluido}
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Número da ordem de compra para esta indústria
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                            Frete (opcional)
                          </label>
                          <select
                            value={pedidoIndustria.frete || ''}
                            onChange={e => {
                              setPedidoIndustrias(prev => prev.map(pi =>
                                pi.industriaId === pedidoIndustria.industriaId
                                  ? { ...pi, frete: e.target.value ? (e.target.value as TipoFrete) : undefined }
                                  : pi
                              ))
                            }}
                            className="flex min-h-[44px] w-full rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-3 text-base text-gray-900 dark:text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all"
                            disabled={isConcluido}
                          >
                            <option value="">Selecione o tipo de frete</option>
                            <option value={'CIF'}>CIF</option>
                            <option value={'FOB'}>FOB</option>
                          </select>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Tipo de frete para esta indústria
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Observação da Indústria */}
                    {pedidoIndustria.industriaId && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                          Observação para esta indústria (opcional)
                        </label>
                        <textarea
                          value={pedidoIndustria.observacao || ''}
                          onChange={e => {
                            setPedidoIndustrias(prev => prev.map(pi =>
                              pi.industriaId === pedidoIndustria.industriaId
                                ? { ...pi, observacao: e.target.value }
                                : pi
                            ))
                          }}
                          rows={3}
                          maxLength={500}
                          className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-base dark:border-gray-600 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed focus:border-primary focus:ring-2 focus:ring-primary transition-all"
                          placeholder="Ex: Embalar separadamente, entregar até 10h, etc..."
                          disabled={isConcluido}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                          {pedidoIndustria.observacao?.length || 0}/500 caracteres
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )})}
            </div>
          </Card>
        )}

        {/* Observações Gerais */}
        <Card title="Observações Gerais">
          <div className="space-y-4">
            {isConcluido && (
              <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <p className="text-sm text-green-800 dark:text-green-200">
                  ✓ Este pedido está concluído e não pode mais ser editado. Use o botão "Reabrir Pedido" acima se necessário.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Observações Gerais (opcional)
              </label>
              <textarea
                value={observacao}
                onChange={e => setObservacao(e.target.value)}
                rows={4}
                maxLength={500}
                className="w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-base dark:border-gray-600 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed focus:border-primary focus:ring-2 focus:ring-primary transition-all"
                placeholder="Observações gerais sobre o pedido..."
                disabled={isConcluido}
                readOnly={isConcluido}
              />
            </div>
          </div>
        </Card>

        {/* Resumo */}
        <Card>
          <div className="space-y-3">
            <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                  Total de Itens:
                </span>
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {getTotalItens()}
                </span>
              </div>
            </div>

            {(() => {
              const valorTotal = pedidoIndustrias.reduce((total, pi) => 
                total + (pi.itens || []).reduce((sum, item) => {
                  const preco = normalizePreco(item.preco as unknown as string | number)
                  return sum + (item.quantidade * (preco || 0))
                }, 0), 0
              )
              
              return valorTotal > 0 ? (
                <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-green-900 dark:text-green-100">
                      Valor Total:
                    </span>
                    <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                      R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              ) : null
            })()}
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => navigate('/pedidos')} className="w-full sm:w-auto order-2 sm:order-1">
              {isConcluido ? 'Voltar' : 'Cancelar'}
            </Button>
            {!isConcluido && (
              <Button onClick={handleSalvarPedido} size="lg" className="w-full sm:w-auto order-1 sm:order-2">
                <ShoppingCart className="mr-2 h-5 w-5" />
                Salvar Alterações
              </Button>
            )}
          </div>
        </Card>
      </div>

      <SelectModal
        isOpen={showClienteModal}
        onClose={() => setShowClienteModal(false)}
        onSelect={handleSelectCliente}
        items={clientes}
        title="Selecionar Cliente"
        searchPlaceholder="Buscar por nome ou email..."
        getItemKey={item => item.id}
        getItemLabel={item => item.razaoSocial}
        getItemDescription={item => item.email || ''}
        selectedId={selectedCliente?.id}
      />

      <MultiSelectModal
        isOpen={showIndustriasModal}
        onClose={() => setShowIndustriasModal(false)}
        onConfirm={handleSelectIndustrias}
        items={industrias}
        title="Selecionar Indústrias"
        searchPlaceholder="Buscar indústria..."
        getItemKey={item => item.id}
        getItemLabel={item => item.nomeFantasia}
        getItemDescription={item => item.cnpj || ''}
        selectedIds={selectedIndustrias.map(i => i.id)}
      />

      <ProductSelectModal
        isOpen={showProdutoModal}
        onClose={() => setShowProdutoModal(false)}
        onConfirm={handleProductsConfirm}
        items={currentIndustriaId ? getProdutosPorIndustria(currentIndustriaId) : []}
        selectedItems={
          currentIndustriaId
            ? (pedidoIndustrias
                .find(pi => pi.industriaId === currentIndustriaId)
                ?.itens || []).map(item => ({
                  id: item.produtoId,
                  codigo: item.produto?.codigo || '',
                  nome: item.produto?.nome || '',
                  quantidade: item.quantidade,
                })) || []
            : []
        }
        title="Selecionar Produtos"
        searchPlaceholder="Buscar por código ou nome..."
      />
    </MainLayout>
  )
}
